import {
  filter,
  first,
  fromEvent, map,
  merge,
  mergeMap,
  of,
  repeat,
  Subject,
  switchMap,
  take, takeUntil, takeWhile,
  tap,
} from 'rxjs';
import {
  adOnlyMetrics,
  getCookie,
  getData,
  metricsLog,
  setData,
  sharedMetrics,
  videoOnlyMetrics
} from './utils';
import { SegmentProps, MetricsStorage, SegmentWithLog } from './types';
import { PixelsVideoEvent } from '../../constants/Video';
import { EventTypeV3 } from '../beacon';

const {
  BEFORE_COMPLETE,
  VIDEO_CHANGE,
  FIRST_FRAME,
  TIME,
  AD_ERROR,
  AD_ENDED,
  AD_TIME,
  AD_STARTED,
  AD_REQUEST,
  BEACON_SENT,
  AD_SKIPPED
} = PixelsVideoEvent;

const videoKeys = [...Object.keys(sharedMetrics), ...Object.keys(videoOnlyMetrics)];
const adKeys = [...Object.keys(sharedMetrics), ...Object.keys(adOnlyMetrics)];

export const postRequest = (sub: Subject<[string, any]>, pixelsUUIDKey: string, typeOfIntegration: string) => {

  const video$ =  sub.pipe(
    tap(([e]) => metricsLog('event type: ', e)),
    filter(([type]) => [FIRST_FRAME].includes(type as PixelsVideoEvent)),
  );

  const ads$ = sub.pipe(
    filter(([type]) => [AD_STARTED, AD_REQUEST].includes(type as PixelsVideoEvent)),
    first()
  );

  const videoCompleted$ = merge(video$, ads$).pipe(
    switchMap(() => merge(
      sub.pipe(
        filter(([type]) => [
          BEFORE_COMPLETE, VIDEO_CHANGE, AD_ENDED, AD_ERROR, AD_SKIPPED
        ].includes(type as PixelsVideoEvent)),
        mergeMap(() => of(1, 0))
      ),
      // only send the payload once when the video is paused, and start again if the playback
      // has moved. This is to stop excessive call to the endpoint
      sub.pipe(
        filter(([type]) => [TIME, AD_TIME].includes(type as PixelsVideoEvent)),
        take(1),
        switchMap(() => fromEvent(window.top.document, 'visibilitychange').pipe(
          takeUntil(sub.pipe(filter(([key]) => key === 'stop_subscription'))),
          filter(() => window.top.document.visibilityState === 'hidden'),
        )),
        take(1),
        repeat(),
        map(() => 1)
      )
    )),
    tap(() => metricsLog('complete: ready to send the beacon'))
  );

  merge(videoCompleted$)
    .pipe(
      takeWhile(Boolean),
      repeat(),
    )
    .subscribe(() => {
      const uuid = getCookie(pixelsUUIDKey);
      const storageKey = `${uuid}-${typeOfIntegration}`;
      const payload: MetricsStorage = getData(storageKey);

      if (payload.video_id?.value === '') {
        return;
      }

      const layerKey = window.top.__pixelsLayerKey || 'pixelsLayer';
      window.top[layerKey] = window.top[layerKey] || [];

      // a. metrics for hero
      let { push: sendMetrics } = window.top[layerKey];
      // b. metrics for vertical video
      const variant = payload.design_variant.value;
      if (variant && typeof window.top[layerKey][variant] !== 'undefined') {
        sendMetrics = window.top[layerKey][variant].push;
      }

      sendMetrics(Object.entries(payload)
        .filter(([key]) => {
          switch (payload.type.value) {
            case EventTypeV3.VIDEO:
              return videoKeys.includes(key);
            case EventTypeV3.AD:
              return adKeys.includes(key);
            default:
              return true;
          }
        })
        .reduce<Record<string, string>>((acc, [k, v]) => {
          acc[k] = (v as SegmentWithLog | SegmentProps).value;
          return acc;
        }, {})
      );

      // send done signal
      sub.next([BEACON_SENT, null]);

      setData(storageKey, {
        ...payload
      });
    });
};